import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  // ── Level-3 sibling-panel state ──────────────────────────────────────────
  /** @type {HTMLElement | null} */
  #activeL3Link = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #l3HideTimer;
  // ─────────────────────────────────────────────────────────────────────────

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);

    // Level-3 sibling-panel: delegated listeners on the header-menu element.
    // mouseover/mouseout bubble so a single listener covers all descendants.
    this.addEventListener('mouseover', this.#onL3MouseOver);
    this.addEventListener('mouseout', this.#onL3MouseOut);
    this.addEventListener('focusin', this.#onL3FocusIn);
    this.addEventListener('focusout', this.#onL3FocusOut);
    this.addEventListener('keydown', this.#onL3KeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.removeEventListener('mouseover', this.#onL3MouseOver);
    this.removeEventListener('mouseout', this.#onL3MouseOut);
    this.removeEventListener('focusin', this.#onL3FocusIn);
    this.removeEventListener('focusout', this.#onL3FocusOut);
    this.removeEventListener('keydown', this.#onL3KeyDown);
    this.#resetL3();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    // Left-align the per-trigger submenu under its trigger item
    if (isDefaultSlot && hasSubmenu) {
      this.#positionSubmenu(item, submenu);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      submenu.style.removeProperty('left');
      submenu.style.removeProperty('right');
      delete submenu.dataset.active;
    }

    // Close any open level-3 panel when the top-level submenu closes
    this.#resetL3();
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  /**
   * Enforce per-trigger positioning for all list-item submenus (both 2-level and 3-level).
   * .menu-list__list-item has position:relative, so left:0 / right:0 are already
   * relative to the trigger's own bounding box — no coordinate arithmetic needed.
   * The viewport overflow check flips left-align → right-align when the panel
   * would bleed past the right viewport edge.
   * @param {HTMLElement} link - The .menu-list__link element (trigger).
   * @param {HTMLElement} submenu - The .menu-list__submenu to position.
   */
  #positionSubmenu = (link, submenu) => {

    submenu.style.left = '0';
    submenu.style.removeProperty('right');

    requestAnimationFrame(() => {
      const rect = submenu.getBoundingClientRect();
      if (rect.right > window.innerWidth - 8) {
        submenu.style.removeProperty('left');
        submenu.style.right = '0';
      }
    });
  };

  // ── Level-3 sibling-panel methods ────────────────────────────────────────

  /**
   * Show the level-3 panel for a level-2 link.
   * @param {HTMLElement} link - Element with data-l3-target attribute.
   */
  #activateL3 = (link) => {
    if (this.#activeL3Link === link) return;
    if (this.#activeL3Link) this.#hideL3Panel(this.#activeL3Link);
    this.#activeL3Link = link;
    link.setAttribute('aria-expanded', 'true');
    const panel = link.dataset.l3Target ? this.querySelector(`#${link.dataset.l3Target}`) : null;
    panel?.removeAttribute('hidden');
    // The l3 panel was display:none when the submenu height was first measured;
    // re-measure so the clip-path rect expands to reveal all l3 items.
    this.#updateSubmenuHeight();
  };

  /**
   * Collapse the level-3 panel for a given link.
   * @param {HTMLElement} link
   */
  #hideL3Panel = (link) => {
    link.setAttribute('aria-expanded', 'false');
    const panel = link.dataset.l3Target ? this.querySelector(`#${link.dataset.l3Target}`) : null;
    panel?.setAttribute('hidden', '');
  };

  /**
   * Re-measure the active submenu and update --submenu-height / --full-open-header-height.
   * Called after an l3 panel is shown or hidden so the clip-path rect bottom stays in sync
   * with the actual rendered height (l3 panels start display:none so the initial measurement
   * in activate() never includes them).
   */
  #updateSubmenuHeight = () => {
    requestAnimationFrame(() => {
      const submenu = findSubmenu(this.#state.activeItem);
      if (!submenu || !this.headerComponent) return;
      const newHeight = submenu.offsetHeight;
      if (newHeight > 0) {
        this.headerComponent.style.setProperty('--submenu-height', `${newHeight}px`);
        this.#setFullOpenHeaderHeight(newHeight, this.#getHeaderVisibleHeight());
      }
    });
  };

  /**
   * Schedule collapse of the active level-3 panel after 200ms (hover-out grace).
   */
  #scheduleL3Hide = () => {
    clearTimeout(this.#l3HideTimer);
    this.#l3HideTimer = setTimeout(this.#resetL3, 200);
  };

  /**
   * Immediately collapse the active level-3 panel and clear all l3 state.
   */
  #resetL3 = () => {
    clearTimeout(this.#l3HideTimer);
    if (this.#activeL3Link) {
      this.#hideL3Panel(this.#activeL3Link);
      this.#activeL3Link = null;
      // Re-measure only when the l1 submenu is still open so the clip-path
      // shrinks back to l2-only height. Skip when called from #deactivate
      // (activeItem is already null there and the height is already set to 0).
      if (this.#state.activeItem) {
        this.#updateSubmenuHeight();
      }
    }
  };

  /**
   * Delegated mouseover — activates l3 panel when cursor enters a [data-l3-target] link,
   * and also clears any pending hide timer when entering the l3 panel itself (safety net
   * for when the cursor crosses the padding gap between the l2 and l3 columns).
   * @param {MouseEvent} event
   */
  #onL3MouseOver = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = /** @type {HTMLElement | null} */ (event.target.closest('[data-l3-target]'));
    if (link) {
      clearTimeout(this.#l3HideTimer);
      this.#activateL3(link);
      return;
    }
    if (event.target.closest('.mega-menu__l3-panel')) {
      clearTimeout(this.#l3HideTimer);
    }
  };

  /**
   * Delegated mouseout — schedules hide when cursor leaves a [data-l3-target] link or l3 panel,
   * unless the cursor is moving to another l3-related element or is still within the
   * .mega-menu__l3-layout container (which covers the padding gap between the two columns).
   * @param {MouseEvent} event
   */
  #onL3MouseOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const fromLink = event.target.closest('[data-l3-target]');
    const fromPanel = event.target.closest('.mega-menu__l3-panel');
    if (!fromLink && !fromPanel) return;

    const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (to?.closest('[data-l3-target]') || to?.closest('.mega-menu__l3-panel')) return;
    // The l3-layout flex container wraps both the l2 column and the l3 panels column,
    // including their padding. Don't start the timer while the cursor is still inside
    // that container — it just means the user is crossing the visual gap between columns.
    const layout = (fromLink ?? fromPanel)?.closest('.mega-menu__l3-layout');
    if (layout && to && layout.contains(to)) return;
    this.#scheduleL3Hide();
  };

  /**
   * Delegated focusin — activates l3 panel when a [data-l3-target] link receives keyboard focus.
   * @param {FocusEvent} event
   */
  #onL3FocusIn = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = /** @type {HTMLElement | null} */ (event.target.closest('[data-l3-target]'));
    if (!link) return;
    clearTimeout(this.#l3HideTimer);
    this.#activateL3(link);
  };

  /**
   * Delegated focusout — schedules hide when focus leaves the l3 area.
   * @param {FocusEvent} event
   */
  #onL3FocusOut = (event) => {
    if (!(event.target instanceof Element)) return;
    const fromLink = event.target.closest('[data-l3-target]');
    const fromPanel = event.target.closest('.mega-menu__l3-panel');
    if (!fromLink && !fromPanel) return;

    const to = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (to?.closest('[data-l3-target]') || to?.closest('.mega-menu__l3-panel')) return;
    this.#scheduleL3Hide();
  };

  /**
   * Keyboard navigation for the level-3 sibling panel.
   * - ArrowRight on a [data-l3-target] link → open panel, focus first item.
   * - ArrowLeft / Escape inside l3 panel → close panel, return focus to l2 link.
   * - ArrowDown / ArrowUp inside l3 panel → move between items; ArrowUp from
   *   first item returns focus to the l2 link.
   * @param {KeyboardEvent} event
   */
  #onL3KeyDown = (event) => {
    if (!(event.target instanceof Element)) return;

    const link = /** @type {HTMLElement | null} */ (event.target.closest('[data-l3-target]'));
    if (link) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#activateL3(link);
        const panel = link.dataset.l3Target ? this.querySelector(`#${link.dataset.l3Target}`) : null;
        /** @type {HTMLElement | null} */ (panel?.querySelector('a'))?.focus();
      }
      return;
    }

    const panel = /** @type {HTMLElement | null} */ (event.target.closest('.mega-menu__l3-panel'));
    if (!panel) return;

    if (event.key === 'Escape' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const l2Link = /** @type {HTMLElement | null} */ (this.querySelector(`[data-l3-target="${panel.id}"]`));
      this.#resetL3();
      l2Link?.focus();
      return;
    }

    const items = /** @type {HTMLElement[]} */ (Array.from(panel.querySelectorAll('a')));
    const currentIndex = items.indexOf(/** @type {HTMLElement} */ (event.target));

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (currentIndex < items.length - 1) {
        items[currentIndex + 1]?.focus();
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (currentIndex > 0) {
        items[currentIndex - 1]?.focus();
      } else {
        // ArrowUp from first l3 item → return to the l2 link
        const l2Link = /** @type {HTMLElement | null} */ (this.querySelector(`[data-l3-target="${panel.id}"]`));
        l2Link?.focus();
      }
    }
  };
  // ─────────────────────────────────────────────────────────────────────────
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
